<template>
  <div class="user-learning-progress">
    <div class="progress-header">
      <h3>我的学习进度</h3>
      <div class="progress-stats">
        <div class="stat-item">
          <span class="stat-number">{{ totalCourses }}</span>
          <span class="stat-label">总课程</span>
        </div>
        <div class="stat-item">
          <span class="stat-number">{{ completedCourses }}</span>
          <span class="stat-label">已完成</span>
        </div>
        <div class="stat-item">
          <span class="stat-number">{{ totalKnowledgePoints }}</span>
          <span class="stat-label">知识点</span>
        </div>
        <div class="stat-item">
          <span class="stat-number">{{ masteredPoints }}</span>
          <span class="stat-label">已学完</span>
        </div>
      </div>
    </div>

    <div class="progress-content">
      <!-- 学习进度概览 -->
      <div class="overview-section">
        <h4>学习概览</h4>
        <div class="overview-cards">
          <div class="overview-card">
            <div class="card-icon">📚</div>
            <div class="card-content">
              <div class="card-title">学习进度</div>
              <div class="card-value">{{ overallProgress }}%</div>
              <el-progress 
                :percentage="overallProgress" 
                :color="getProgressColor(overallProgress)"
                :stroke-width="8"
              />
            </div>
          </div>
          
          <div class="overview-card">
            <div class="card-icon">🎯</div>
            <div class="card-content">
              <div class="card-title">掌握程度</div>
              <div class="card-value">{{ masteryRate }}%</div>
              <el-progress 
                :percentage="masteryRate" 
                :color="getProgressColor(masteryRate)"
                :stroke-width="8"
              />
              <p class="mastery-note">{{ masteryNote }}</p>
            </div>
          </div>
          
        </div>
      </div>

      <!-- 课程学习列表 -->
      <div class="courses-section">
        <h4>我的课程</h4>
        <div v-if="loading" class="loading">
          <el-skeleton :rows="3" animated />
        </div>
        <div v-else-if="userCourses.length === 0" class="empty-state">
          <el-empty description="还没有开始学习任何课程" />
          <el-button type="primary" @click="goToCourses">去选择课程</el-button>
        </div>
        <div v-else class="courses-list">
          <div 
            v-for="course in userCourses" 
            :key="course.kcId" 
            class="course-item"
            @click="viewCourse(course)"
          >
            <div class="course-info">
              <div class="course-header">
                <h5 class="course-name">{{ course.name }}</h5>
                <div class="course-badges">
                  <el-tag :type="getLevelTagType(course.level)" size="small">
                    {{ getLevelText(course.level) }}
                  </el-tag>
                  <el-tag type="info" size="small">{{ course.category }}</el-tag>
                </div>
              </div>
              
              <div class="course-progress">
                <div class="progress-info">
                  <span class="progress-text">学习进度: {{ getCourseProgress(course) }}%</span>
                  <span class="progress-detail">
                    {{ getCompletedPoints(course) }}/{{ getTotalPoints(course) }} 知识点
                  </span>
                </div>
                <el-progress 
                  :percentage="getCourseProgress(course)" 
                  :color="getProgressColor(getCourseProgress(course))"
                  :stroke-width="6"
                />
              </div>
              
              <div class="course-actions">
                <el-button type="primary" size="small" @click.stop="continueLearning(course)">
                  {{ getCourseProgress(course) > 0 ? '继续学习' : '开始学习' }}
                </el-button>
                <el-button type="info" size="small" @click.stop="viewCourse(course)">
                  查看详情
                </el-button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 最近掌握的知识点 -->
      <div class="mastered-section">
        <h4>最近学习完成</h4>
        <div v-if="recentlyMastered.length === 0" class="empty-mastered">
          <p>还没有学习完成的知识点</p>
        </div>
        <div v-else class="mastered-list">
          <div 
            v-for="item in recentlyMastered" 
            :key="item.id" 
            class="mastered-item"
          >
            <div class="mastered-icon">✅</div>
            <div class="mastered-content">
              <div class="mastered-name">{{ item.knowledgePointName }}</div>
              <div class="mastered-meta">
                <span class="mastered-course">{{ item.courseName }}</span>
                <span class="mastered-score">{{ learnStatusText(item) }} · {{ item.score }}/100</span>
                <span class="mastered-date">{{ formatDate(item.masteredAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UserLearningProgress',
  data() {
    return {
      loading: true,
      userCourses: [],
      recentlyMastered: [],
      masteredScores: {},
      examDoneIds: {},
      totalCourses: 0,
      completedCourses: 0,
      totalKnowledgePoints: 0,
      masteredPoints: 0
    }
  },
  computed: {
    overallProgress() {
      if (this.totalCourses === 0) return 0
      return Math.round((this.completedCourses / this.totalCourses) * 100)
    },
    linkedExamCourses() {
      return this.userCourses.filter((course) => this.courseExamIds(course).length > 0)
    },
    masteryRate() {
      if (!this.userCourses.length) return 0
      const linked = this.linkedExamCourses
      if (!linked.length) return 100
      const sum = linked.reduce((acc, course) => acc + this.courseExamMastery(course), 0)
      return Math.round(sum / linked.length)
    },
    masteryNote() {
      if (!this.userCourses.length) return '还没有课程'
      if (!this.linkedExamCourses.length) {
        return '这些课程都没有关联测试，掌握度为 100%。看完教程只更新学习进度。'
      }
      return '掌握度只按有关联测试的课程计算。没有测试的课程看完后学习进度完成，不改变掌握度。'
    }
  },
  created() {
    this.loadUserProgress()
  },
  methods: {
    async loadUserProgress() {
      try {
        this.loading = true
        await Promise.all([
          this.loadRecentlyMastered(),
          this.loadExamDoneIds()
        ])
        await this.loadUserCourses()
        this.computeProgressStats()
      } catch (error) {
        console.error('Failed to load user progress:', error)
        this.$message.error('加载学习进度失败')
      } finally {
        this.loading = false
      }
    },

    async loadExamDoneIds() {
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/result/list', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ examId: 0 })
        })
        const data = await response.json()
        const map = {}
        if (data.code === 200) {
          const results = data.data?.results || []
          results.forEach((row) => {
            const examId = Number(row.examId || 0)
            if (examId > 0 && Number(row.isTemp || 0) === 0) {
              map[examId] = true
            }
          })
        }
        this.examDoneIds = map
      } catch (error) {
        console.error('Failed to load exam results:', error)
        this.examDoneIds = {}
      }
    },

    courseExamIds(course) {
      const raw = course?.examIds || course?.relatedExamIds || []
      if (!Array.isArray(raw)) return []
      return raw.map((id) => Number(id)).filter((id) => id > 0)
    },

    courseExamMastery(course) {
      const ids = this.courseExamIds(course)
      if (!ids.length) return 0
      const done = ids.filter((id) => this.examDoneIds[id]).length
      return Math.round((done / ids.length) * 100)
    },

    learnStatusText(item) {
      if (item.status === 'mastered' || (item.score || 0) >= 100) return '已经学习完成'
      if (item.status === 'learned' || (item.score || 0) >= 67) return '已学习'
      if (item.status === 'started' || (item.score || 0) >= 34) return '已开始'
      return item.status || `得分 ${item.score}`
    },
    
    async loadUserCourses() {
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/courses/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            page: 1,
            pageSize: 1000,
            keyword: ''
          })
        })
        
        const data = await response.json()
        if (data.code === 200) {
          const all = data.data.list || []
          const myUserId = this.getUserId()
          this.userCourses = all.filter((course) => {
            if ((course.isAcquired || 0) > 0) return true
            if (myUserId && Number(course.userId) === Number(myUserId)) return true
            return this.getCompletedPoints(course) > 0
          })
          this.totalCourses = this.userCourses.length
        }
      } catch (error) {
        console.error('Failed to load user courses:', error)
      }
    },
    
    async loadRecentlyMastered() {
      try {
        const token = localStorage.getItem('jwt_token')
        const response = await fetch('/api/v1/knowledge/mastered/list', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            page: 1,
            pageSize: 500
          })
        })
        
        const data = await response.json()
        if (data.code === 200) {
          const list = data.data.list || []
          const map = {}
          list.forEach((row) => {
            if (row.kpId) {
              map[row.kpId] = row.score || 0
            }
          })
          this.masteredScores = map
          this.recentlyMastered = list.filter((row) => (row.score || 0) >= 100)
          this.masteredPoints = this.recentlyMastered.length
        }
      } catch (error) {
        console.error('Failed to load recently mastered:', error)
        this.recentlyMastered = []
        this.masteredScores = {}
        this.masteredPoints = 0
      }
    },

    computeProgressStats() {
      this.totalKnowledgePoints = this.userCourses.reduce((total, course) => {
        return total + this.getTotalPoints(course)
      }, 0)
      this.completedCourses = this.userCourses.filter((course) => {
        return this.getCourseProgress(course) >= 100
      }).length
    },
    
    getUserId() {
      // 从localStorage或用户信息中获取用户ID
      const userInfo = localStorage.getItem('user_info')
      if (userInfo) {
        try {
          return JSON.parse(userInfo).userId || 0
        } catch {
          return 0
        }
      }
      return 0
    },
    
    getCourseProgress(course) {
      if (course.progressPct != null && course.progressPct > 0) {
        return Math.min(100, Number(course.progressPct) || 0)
      }
      const ids = this.collectKpIds(course)
      if (!ids.length) return 0
      const sum = ids.reduce((acc, id) => acc + (this.masteredScores[id] || 0), 0)
      return Math.round(sum / ids.length)
    },
    
    getTotalPoints(course) {
      if (!course.kpTree) return 0
      try {
        const tree = JSON.parse(course.kpTree)
        return this.countNodes(tree)
      } catch {
        return 0
      }
    },
    
    getCompletedPoints(course) {
      const ids = this.collectKpIds(course)
      return ids.filter((id) => (this.masteredScores[id] || 0) >= 100).length
    },

    collectKpIds(course) {
      const tree = this.parseKpTree(course)
      return this.collectKpIdsFromTree(tree)
    },

    parseKpTree(course) {
      if (!course?.kpTree) return null
      if (typeof course.kpTree === 'object') return course.kpTree
      try {
        return JSON.parse(course.kpTree)
      } catch {
        return null
      }
    },

    collectKpIdsFromTree(tree) {
      const ids = []
      const walk = (node) => {
        if (!node) return
        const id = Number(node.kpID || node.kpId || 0)
        if (id > 0) ids.push(id)
        if (Array.isArray(node.children)) {
          node.children.forEach(walk)
        }
      }
      walk(tree)
      return ids
    },
    
    countNodes(tree) {
      return this.collectKpIdsFromTree(tree).length
    },
    
    getLevelText(level) {
      const levels = {
        1: '初级',
        2: '中级',
        3: '高级'
      }
      return levels[level] || '未知'
    },
    
    getLevelTagType(level) {
      const types = {
        1: 'success',
        2: 'warning',
        3: 'danger'
      }
      return types[level] || 'info'
    },
    
    getProgressColor(percentage) {
      if (percentage >= 80) return '#67C23A'
      if (percentage >= 60) return '#E6A23C'
      if (percentage >= 40) return '#F56C6C'
      return '#909399'
    },
    
    formatDate(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('zh-CN')
    },
    
    viewCourse(course) {
      this.$router.push(`/knowledge/courses/display/${course.kcId}`)
    },
    
    continueLearning(course) {
      this.viewCourse(course)
    },
    
    goToCourses() {
      this.$router.push('/knowledge/courses')
    }
  }
}
</script>

<style scoped>
.user-learning-progress {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

.progress-header {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.progress-header h3 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 20px;
}

.progress-stats {
  display: flex;
  gap: 30px;
}

.stat-item {
  text-align: center;
}

.stat-number {
  display: block;
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #7f8c8d;
}

.progress-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.overview-section,
.courses-section,
.mastered-section {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.overview-section h4,
.courses-section h4,
.mastered-section h4 {
  margin: 0 0 20px 0;
  color: #2c3e50;
  font-size: 18px;
}

.overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.overview-card {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #409EFF;
}

.card-icon {
  font-size: 32px;
}

.card-content {
  flex: 1;
}

.card-title {
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 8px;
}

.card-value {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 8px;
}

.card-subtitle {
  font-size: 12px;
  color: #95a5a6;
}

.courses-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.course-item {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.course-item:hover {
  border-color: #409EFF;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.1);
}

.course-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.course-name {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
  flex: 1;
  margin-right: 15px;
}

.course-badges {
  display: flex;
  gap: 8px;
}

.course-progress {
  margin-bottom: 15px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.progress-text {
  font-size: 14px;
  color: #2c3e50;
  font-weight: 500;
}

.progress-detail {
  font-size: 12px;
  color: #7f8c8d;
}

.course-actions {
  display: flex;
  gap: 10px;
}

.mastered-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mastered-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #67C23A;
}

.mastered-icon {
  font-size: 20px;
}

.mastered-content {
  flex: 1;
}

.mastered-name {
  font-size: 14px;
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 4px;
}

.mastered-meta {
  display: flex;
  gap: 15px;
  font-size: 12px;
  color: #7f8c8d;
}

.mastered-course {
  color: #409EFF;
}

.mastered-score {
  color: #67C23A;
  font-weight: 500;
}

.mastery-note {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 1.4;
  color: #909399;
}

.loading {
  padding: 20px;
}

.empty-state,
.empty-mastered {
  text-align: center;
  padding: 40px 20px;
  color: #7f8c8d;
}

.empty-state p,
.empty-mastered p {
  margin: 0 0 15px 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .progress-stats {
    flex-wrap: wrap;
    gap: 20px;
  }
  
  .overview-cards {
    grid-template-columns: 1fr;
  }
  
  .course-header {
    flex-direction: column;
    gap: 10px;
  }
  
  .course-badges {
    align-self: flex-start;
  }
  
  .course-actions {
    flex-direction: column;
  }
  
  .mastered-meta {
    flex-direction: column;
    gap: 5px;
  }
}
</style> 